import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/20/solid';
import VendorEditModal from '../components/VendorEditModal';

export default function Vendors() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  useEffect(() => {
    document.title = 'Vendors';
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('suppliers')
      .select(
        'id, name, vendor_code, contact_name, contact_email, contact_phone, address, city, state, zip, payment_terms, default_ship_via, notes, is_active, created_at'
      )
      .order('name');
    if (error) {
      console.error(error);
      toast.error('Failed to load vendors.');
    } else {
      setVendors(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user) fetchVendors();
  }, [user]);

  const filtered = vendors.filter((v) => {
    if (!showInactive && !v.is_active) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (v.name || '').toLowerCase().includes(q) ||
      (v.vendor_code || '').toLowerCase().includes(q) ||
      (v.contact_name || '').toLowerCase().includes(q) ||
      (v.city || '').toLowerCase().includes(q)
    );
  });

  const openNew = () => {
    setEditingVendor(null);
    setModalOpen(true);
  };
  const openEdit = (v) => {
    setEditingVendor(v);
    setModalOpen(true);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-slate-800">Vendors</h1>
        <button
          onClick={openNew}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800"
        >
          <PlusIcon className="w-4 h-4" /> New Vendor
        </button>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code, contact, city…"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded-sm border-slate-300"
          />
          Show inactive
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">
                Name
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">
                Code
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">
                Contact
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">
                Location
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">
                Terms
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">
                Status
              </th>
              <th className="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">
                  No vendors found.
                </td>
              </tr>
            ) : (
              filtered.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm text-slate-800">{v.name}</td>
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {v.vendor_code || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {v.contact_name || '-'}
                    {v.contact_email && (
                      <span className="block text-xs text-slate-400">
                        {v.contact_email}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {[v.city, v.state].filter(Boolean).join(', ') || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {v.payment_terms || '-'}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {v.is_active ? (
                      <span className="px-2 py-0.5 text-xs rounded-sm bg-emerald-50 text-emerald-700">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs rounded-sm bg-slate-100 text-slate-500">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openEdit(v)}
                      className="p-1 text-slate-400 hover:text-slate-700"
                      aria-label="Edit vendor"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <VendorEditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        vendor={editingVendor}
        onSaved={() => {
          fetchVendors();
        }}
      />
    </div>
  );
}
